import React, { forwardRef, useLayoutEffect, useRef } from "react";
import { FiCheck, FiAward } from "react-icons/fi";
import { FaFire } from "react-icons/fa";

import {
  safeAreaVarsDoFormato,
  FORMATOS,
} from "../../../../config/storyCanvas";
import { formatKcal } from "../../../../utils/formatKcal";
import {
  resolveWorkoutMetrics,
  summarizeWorkout,
  activityDurationMin,
} from "../../../treinos/utils/workoutMetrics";
import {
  ATTR_SEL,
  RAIZ,
  aplicarAjustes,
  caminhoDoElemento,
  estudioApi,
  garantirVarsBase,
  limparEstudio,
  patchCompleto,
  resolverElemento,
  useEstudioDoCard,
} from "./shareStudio";
import Style from "../../../../styles/features/nutricao/ShareRefeicao.module.css";

// Zona útil do Story: as margens entram no card como CSS vars (fonte única em
// config/storyCanvas.js) e o SCSS posiciona TODO conteúdo informativo com
// var(--story-safe-*). Objeto único — não muda entre renders.
/* Um mapa por formato, calculado uma vez: o cartão de feed tem zona útil
   menor (o corte 3:4 da grade do perfil come as pontas). */
const SAFE_AREA_VARS_POR_FORMATO = {
  STORY: safeAreaVarsDoFormato("STORY"),
  FEED: safeAreaVarsDoFormato("FEED"),
};

export const MEAL_CARD_ORDER = ["1a", "1b", "1i", "1c", "1d", "1e", "1f", "1h"];
export const WORKOUT_CARD_ORDER = [
  "1q",
  "1l",
  "1m",
  "1k",
  "1n",
  "1o",
  "1p",
  "1r",
  "1s",
];

/**
 * As artes portadas para o formato de FEED (4:5).
 *
 * QUATRO, não dezessete. Cada porte é um relayout conferido no PNG baixado —
 * a altura útil do cartão 4:5 é ~30% menor que a do 9:16, e o que cabia em
 * duas linhas passa a caber em uma. Portar as dezessete de uma vez entregaria
 * treze artes tortas com cara de prontas. As duas melhores de cada lado
 * abrem o formato; a estrutura aceita o resto sem mudança nenhuma.
 */
export const MEAL_CARD_ORDER_FEED = ["1a", "1b"];
export const WORKOUT_CARD_ORDER_FEED = ["1q", "1l"];

/**
 * As artes da atividade GPS (traçado + métricas), do desenho "Compartilhar
 * Atividade". Entram NA FRENTE das de treino, e só quando a origem é uma
 * atividade com traçado (`data.gps`, ver features/atividade/arteGps.js) —
 * treino sem GPS e refeição não as veem. `1t` é a principal (escura, sobre a
 * foto ou sticker transparente, conforme a tela); `1u` é a versão marca.
 * Existem nos dois formatos: o layout é uma coluna centralizada que se ajusta
 * à altura do cartão.
 */
export const GPS_CARD_ORDER = ["1t", "1u"];

/**
 * A lista de artes de um tipo, no formato pedido.
 *
 * @param {string} tipo 'meal' | 'workout'
 * @param {string} formato 'STORY' | 'FEED'
 * @returns {string[]} ids das artes
 */
export function ordemDasArtes(tipo, formato) {
  /* Conquista compartilha as artes de treino: as duas têm a mesma forma
     (título + até quatro métricas + foto). Ver buildAchievementShareData. */
  const ehTreino = tipo === "workout" || tipo === "achievement";
  if (formato === "FEED") {
    return ehTreino ? WORKOUT_CARD_ORDER_FEED : MEAL_CARD_ORDER_FEED;
  }
  return ehTreino ? WORKOUT_CARD_ORDER : MEAL_CARD_ORDER;
}

const safeNumber = (value) => {
  const normalized =
    typeof value === "string" ? value.trim().replace(",", ".") : value;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Macros (g), streak e recorde continuam no formato pt-BR; kcal usa formatKcal.
const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(
    Math.round(safeNumber(value))
  );

const nutrientAmount = (apiResult, key) =>
  safeNumber(apiResult?.nutrients?.[key]?.amount);

export const buildMealShareData = ({
  capturedImage,
  apiResult,
  translatedName,
} = {}) => ({
  kind: "meal",
  imageUrl: capturedImage || "",
  title: translatedName || apiResult?.mealTitle || "Minha refeição",
  calories: nutrientAmount(apiResult, "calories"),
  protein: nutrientAmount(apiResult, "protein"),
  carbs: nutrientAmount(apiResult, "carbohydrates"),
  fat: nutrientAmount(apiResult, "fat"),
  period: apiResult?.mealPeriod || apiResult?.meal_period || "REFEIÇÃO",
});

// ── Treino ───────────────────────────────────────────────────────────────────
// As métricas chegam PRONTAS do resolvedor único (treinos/utils/workoutMetrics):
// no máximo 4, sem repetição, sem placeholder, escolhidas pelo activity_key.
// Os layouts abaixo só distribuem a lista — nenhum decide o que mostrar. O
// nome do treino vai no título; "grupos musculares" saiu do padrão.
export const buildWorkoutShareData = (workout = {}, profileData = {}) => {
  const activities = Array.isArray(workout?.atividades)
    ? workout.atividades
    : [];
  const metrics = resolveWorkoutMetrics(workout, profileData);
  const { totals } = summarizeWorkout(workout, profileData);
  return {
    kind: "workout",
    imageUrl: workout?.photo_url || workout?.capturedImage || "",
    title: workout?.title || "Treino concluído",
    activities,
    metrics,
    hero: metrics[0] || null,
    rest: metrics.slice(1),
    duration: totals.duration,
    xp: safeNumber(workout?.xp_earned),
    streak: safeNumber(
      workout?.streak ??
        profileData?.XP_VITA?.STREAK_DIAS ??
        profileData?.streak
    ),
    personalRecord: workout?.personal_record || workout?.record || null,
  };
};

// ── Conquista: ofensiva, marco e vitória semanal ─────────────────────────────
//
// POR QUE REUSAR OS LAYOUTS DE TREINO, e não desenhar dezessete artes novas:
// as artes existentes já sabem distribuir "título + até quatro métricas +
// foto", que é exatamente a forma de uma conquista. O que falta não é layout,
// é CONTEÚDO — e conteúdo é este builder. Uma família de artes própria seria
// dezessete relayouts para dizer a mesma coisa com outra moldura.
//
// Estes são os momentos de maior orgulho do app e não tinham arte nenhuma:
// quem fecha 30 dias de ofensiva ou ganha a semana no clube não tinha o que
// mostrar. É também o que nos diferencia de um Instagram genérico dentro do
// app — a foto de prato qualquer rede faz; "100 dias seguidos" não.
export const buildAchievementShareData = (conquista = {}, profileData = {}) => {
  const tipo = conquista.tipo || "STREAK";
  const metricas = [];

  if (tipo === "STREAK") {
    metricas.push({
      key: "streak",
      value: safeNumber(conquista.dias),
      label: "dias seguidos",
    });
    if (conquista.melhor) {
      metricas.push({
        key: "melhor",
        value: safeNumber(conquista.melhor),
        label: "seu recorde",
      });
    }
  } else if (tipo === "VITORIA") {
    metricas.push({
      key: "posicao",
      value: `${safeNumber(conquista.posicao) || 1}º`,
      label: "no clube",
    });
    if (conquista.pontos) {
      metricas.push({
        key: "pontos",
        value: safeNumber(conquista.pontos),
        label: "pontos",
      });
    }
  } else if (conquista.valor != null) {
    metricas.push({
      key: "marco",
      value: safeNumber(conquista.valor),
      label: conquista.rotulo || "",
    });
  }

  const nivel = safeNumber(profileData?.XP_VITA?.LEVEL ?? conquista.nivel);
  if (nivel) metricas.push({ key: "nivel", value: nivel, label: "nível" });

  return {
    /* `kind: 'workout'` de propósito: é o contrato que os layouts 1k…1s já
       leem. A conquista não é um treino, mas tem a MESMA forma — e fingir um
       terceiro tipo obrigaria a tocar em nove artes para não mudar nada. */
    kind: "workout",
    imageUrl: conquista.imageUrl || profileData?.IMAGE_USER || "",
    title: conquista.titulo || "Conquista desbloqueada",
    activities: [],
    metrics: metricas.slice(0, 4),
    hero: metricas[0] || null,
    rest: metricas.slice(1, 4),
    duration: null,
    xp: safeNumber(conquista.xp),
    streak: safeNumber(conquista.dias ?? profileData?.XP_VITA?.STREAK_DIAS),
    personalRecord: conquista.recorde || null,
  };
};

// `data-el` batiza o bloco para o ESTÚDIO (shareStudio.js): é o que deixa o
// caminho salvo legível ("bloco-inferior/stat-proteina") e estável quando a
// arte renderiza com quantidades diferentes de métricas. Nome vem do papel do
// bloco, nunca da classe do CSS — classe de CSS Module é hasheada no build.
// NFD separa a letra do acento; o acento sobra fora do ASCII e some no filtro.
const apelido = (texto) =>
  String(texto || "item")
    .normalize("NFD")
    .replace(/[^\x20-\x7E]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "item";

const Logo = ({ dark = false }) => (
  <span
    data-el="logo"
    className={`${Style.logo} ${dark ? Style.logoDark : ""}`}
    aria-label="LesVita"
  >
    <span>Les</span>
    <span>Vita</span>
  </span>
);

const Stat = ({ value, label, accent = false, nome }) => (
  <div data-el={nome || `stat-${apelido(label)}`} className={Style.stat}>
    <strong className={accent ? Style.accentText : ""}>{value}</strong>
    <span>{label}</span>
  </div>
);

// Uma métrica do resolvedor. `compact` mostra só o número e a unidade vira o
// rótulo (barras estreitas). O nome usa `metric.key`, não o rótulo: a chave é
// estável entre modalidades, o rótulo muda com o idioma do dado.
const MetricStat = ({ metric, compact = false }) => (
  <Stat
    nome={`stat-${apelido(metric.key)}`}
    value={compact ? metric.value : metric.text}
    label={compact ? metric.unit || metric.label : metric.label}
  />
);

const metricsLine = (metrics) => metrics.map((m) => m.text).join(" · ");

const CardTitle = ({ children }) => (
  <span data-el="titulo" className={Style.cardTitle}>
    {children}
  </span>
);

const Separator = () => (
  <span data-el="separador" className={Style.separator} aria-hidden="true" />
);

const Ring = ({ value, unit, progress = 0.82 }) => {
  const circumference = 283;
  const dash = Math.max(20, Math.min(circumference, circumference * progress));
  return (
    <div data-el="anel" className={Style.ring}>
      <svg viewBox="0 0 104 104" aria-hidden="true">
        <circle cx="52" cy="52" r="45" className={Style.ringTrack} />
        <circle
          cx="52"
          cy="52"
          r="45"
          className={Style.ringValue}
          strokeDasharray={`${dash} ${circumference}`}
          transform="rotate(-90 52 52)"
        />
      </svg>
      <div data-el="valor-anel" className={Style.ringLabel}>
        <strong>{value}</strong>
        <span>{unit}</span>
      </div>
    </div>
  );
};

const MealCard = ({ variant, data }) => {
  // kcal: inteiro, sem separador de milhar (utils/formatKcal); macros em g
  // continuam no formato pt-BR.
  const calories = formatKcal(data.calories);
  const protein = `${formatNumber(data.protein)}g`;
  const carbs = `${formatNumber(data.carbs)}g`;
  const fat = `${formatNumber(data.fat)}g`;

  if (variant === "1b")
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientLeft}`}
        />
        <div data-el="bloco-esquerda" className={Style.leftStats}>
          <Stat value={protein} label="Proteína" />
          <Stat value={carbs} label="Carbo" />
          <Stat value={fat} label="Gorduras" />
          <Stat value={`${calories} kcal`} label="Calorias" />
          <Logo />
        </div>
      </>
    );

  if (variant === "1i")
    return (
      <>
        <Logo />
        <div data-el="trilho-lateral" className={Style.sideRail}>
          <Stat value={calories} label="kcal" accent />
          <Separator />
          <Stat value={protein} label="prot" />
          <Stat value={carbs} label="carbo" />
          <Stat value={fat} label="gord" />
        </div>
      </>
    );

  if (variant === "1c")
    return (
      <>
        <Logo />
        <div data-el="barra-vidro" className={Style.glassBar}>
          <Stat value={calories} label="kcal" />
          <Separator />
          <Stat value={protein} label="prot" />
          <Separator />
          <Stat value={carbs} label="carbo" />
          <Separator />
          <Stat value={fat} label="gord" />
        </div>
      </>
    );

  if (variant === "1d")
    return (
      <div data-el="cartao-branco" className={Style.whiteCard}>
        <div data-el="cabecalho-branco" className={Style.whiteHeader}>
          <span>SCANFOOD · {String(data.period).toUpperCase()}</span>
          <Logo />
        </div>
        <strong data-el="titulo-branco" className={Style.whiteTitle}>
          {calories} kcal
        </strong>
        <div data-el="regua-branca" className={Style.whiteRule} />
        <div data-el="stats-brancos" className={Style.whiteStats}>
          <Stat value={protein} label="Proteína" />
          <Stat value={carbs} label="Carboidratos" />
          <Stat value={fat} label="Gorduras" />
        </div>
      </div>
    );

  if (variant === "1e")
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientBottom}`}
        />
        <Logo />
        <div data-el="bloco-anel" className={Style.ringBlock}>
          <Ring
            value={calories}
            unit="kcal"
            progress={data.calories / Math.max(data.calories, 1300)}
          />
          <div data-el="chips" className={Style.chips}>
            <span>P {protein}</span>
            <span>C {carbs}</span>
            <span>G {fat}</span>
          </div>
        </div>
      </>
    );

  if (variant === "1f")
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientBottom}`}
        />
        <div data-el="bloco-minimo" className={Style.minimal}>
          <div>
            <strong>{calories}</strong>
            <b>kcal</b>
            <span>
              P {protein} · C {carbs} · G {fat}
            </span>
          </div>
          <Logo />
        </div>
      </>
    );

  if (variant === "1h")
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientTop}`}
        />
        <div data-el="faixa-topo" className={Style.topBanner}>
          <div>
            <Logo />
            <b>· SCANFOOD</b>
          </div>
          <strong>{calories} kcal</strong>
          <span>
            {protein} proteína / {carbs} carbo / {fat} gordura
          </span>
        </div>
      </>
    );

  return (
    <>
      <div
        data-el="fundo"
        className={`${Style.gradient} ${Style.gradientBottom}`}
      />
      <div data-el="bloco-inferior" className={Style.bottomPills}>
        <div data-el="linha-pilulas" className={Style.pillRow}>
          <Stat value={protein} label="Proteína" />
          <Stat value={carbs} label="Carboidratos" />
          <Stat value={fat} label="Gorduras" />
        </div>
        <div data-el="rodape-info" className={Style.bottomMeta}>
          <div>
            <span>CALORIAS</span>
            <strong>{calories} kcal</strong>
          </div>
          <Logo />
        </div>
      </div>
    </>
  );
};

// Cada layout renderiza `metrics` (1 a 4 itens) com CSS que se equilibra
// sozinho (colunas iguais, chips centralizados). Sem métrica nenhuma o card
// fica só com o título — melhor que slot vazio.
const WorkoutCard = ({ variant, data }) => {
  const { metrics, hero, rest, title } = data;
  const line = metricsLine(metrics);
  const heroMeta = [title, ...rest.map((m) => m.text)]
    .filter(Boolean)
    .join(" · ");

  if (variant === "1l")
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientLeft}`}
        />
        <div data-el="bloco-esquerda" className={Style.leftStats}>
          <CardTitle>{title}</CardTitle>
          {metrics.map((m) => (
            <MetricStat key={m.key} metric={m} />
          ))}
          <Logo />
        </div>
      </>
    );

  if (variant === "1m")
    return (
      <>
        <Logo />
        <div
          data-el="barra-vidro"
          className={`${Style.glassBar} ${Style.glassBarStack}`}
        >
          <span data-el="titulo-vidro" className={Style.glassTitle}>
            {title}
          </span>
          {metrics.length > 0 && (
            <div data-el="stats-vidro" className={Style.glassStats}>
              {metrics.map((m, index) => (
                <React.Fragment key={m.key}>
                  {index > 0 && <Separator />}
                  <MetricStat metric={m} compact />
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </>
    );

  if (variant === "1k")
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientBottom}`}
        />
        <div data-el="bloco-inferior" className={Style.bottomPills}>
          <CardTitle>{title}</CardTitle>
          {rest.length > 0 && (
            <div data-el="linha-pilulas" className={Style.pillRow}>
              {rest.map((m) => (
                <MetricStat key={m.key} metric={m} />
              ))}
            </div>
          )}
          <div data-el="rodape-info" className={Style.bottomMeta}>
            {hero ? (
              <div>
                <span>{hero.label.toUpperCase()}</span>
                <strong>{hero.text}</strong>
              </div>
            ) : (
              <div>
                <span>TREINO CONCLUÍDO</span>
              </div>
            )}
            <Logo />
          </div>
        </div>
      </>
    );

  if (variant === "1n")
    return (
      <div data-el="cartao-branco" className={Style.whiteCard}>
        <div data-el="cabecalho-branco" className={Style.whiteHeader}>
          <span>TREINO CONCLUÍDO</span>
          <Logo />
        </div>
        <strong
          data-el="titulo-branco"
          className={`${Style.whiteTitle} ${Style.workoutTitle}`}
        >
          {title}
        </strong>
        {metrics.length > 0 && (
          <>
            <div data-el="regua-branca" className={Style.whiteRule} />
            <div data-el="stats-brancos" className={Style.whiteStats}>
              {metrics.map((m) => (
                <MetricStat key={m.key} metric={m} />
              ))}
            </div>
          </>
        )}
      </div>
    );

  if (variant === "1o")
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientBottom}`}
        />
        <Logo />
        <div data-el="bloco-ofensiva" className={Style.streakBlock}>
          <div data-el="pilula-ofensiva" className={Style.streakPill}>
            <FaFire />
            <span>
              {data.streak > 0
                ? `${formatNumber(data.streak)} ${
                    data.streak === 1 ? "dia" : "dias"
                  } de ofensiva`
                : "Treino concluído"}
            </span>
          </div>
          <span>{[title, line].filter(Boolean).join(" · ")}</span>
        </div>
      </>
    );

  if (variant === "1p") {
    const record = data.personalRecord;
    const recordTitle = record?.title || record?.exercise || title;
    const value = record?.value
      ? `${formatNumber(record.value)} ${record.unit || "kg"}`
      : hero?.text;
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientBottom}`}
        />
        <Logo />
        <div data-el="cartao-recorde" className={Style.recordCard}>
          <div>
            <FiAward />
            <span>
              {record ? "NOVO RECORDE PESSOAL" : "DESTAQUE DO TREINO"}
            </span>
          </div>
          <strong>{recordTitle}</strong>
          {value && <b>{value}</b>}
        </div>
      </>
    );
  }

  if (variant === "1r")
    return (
      <div data-el="cartao-lista" className={Style.checklistCard}>
        <div data-el="cabecalho-lista" className={Style.checklistHeader}>
          <strong>{title}</strong>
          <Logo />
        </div>
        <div data-el="itens-lista" className={Style.checklistItems}>
          {data.activities.slice(0, 4).map((activity, index) => {
            const minutes = activityDurationMin(activity);
            return (
              <div key={`${activity?.activity_key || index}`}>
                <FiCheck />
                <span>
                  {activity?.activity_label || activity?.label || "Atividade"}
                  {minutes > 0 ? ` · ${formatNumber(minutes)}min` : ""}
                </span>
              </div>
            );
          })}
          {data.activities.length === 0 && (
            <div>
              <FiCheck />
              <span>Treino finalizado</span>
            </div>
          )}
        </div>
        {line && (
          <>
            <div data-el="regua-lista" className={Style.checklistRule} />
            <span data-el="meta-lista" className={Style.checklistMeta}>
              {line}
            </span>
          </>
        )}
      </div>
    );

  if (variant === "1s")
    return (
      <>
        <div
          data-el="fundo"
          className={`${Style.gradient} ${Style.gradientBottom}`}
        />
        <Logo />
        <div data-el="bloco-anel" className={Style.ringBlock}>
          <CardTitle>{title}</CardTitle>
          {hero && (
            <Ring
              value={hero.value}
              unit={hero.unit || hero.label}
              progress={
                hero.key === "duration"
                  ? data.duration / Math.max(data.duration, 60)
                  : 0.82
              }
            />
          )}
          {rest.length > 0 && (
            <div data-el="chips" className={Style.chips}>
              {rest.map((m) => (
                <span key={m.key}>{m.text}</span>
              ))}
            </div>
          )}
        </div>
      </>
    );

  // '1q' — minimalista: métrica principal grande, o resto numa linha.
  return (
    <>
      <div
        data-el="fundo"
        className={`${Style.gradient} ${Style.gradientBottom}`}
      />
      <div data-el="bloco-minimo" className={Style.minimal}>
        <div>
          {hero ? (
            <>
              <strong>{hero.value}</strong>
              <b>{hero.unit || hero.label}</b>
              <span>{heroMeta}</span>
            </>
          ) : (
            <strong className={Style.minimalTitle}>{title}</strong>
          )}
        </div>
        <Logo />
      </div>
    </>
  );
};

// ── Atividade GPS ────────────────────────────────────────────────────────────
// Desenho "Compartilhar Atividade": métricas empilhadas no centro, traçado
// amarelo embaixo, LesVita no rodapé. O tema sai do que a tela já tem:
//   • 'sticker' — sem fundo (1a do desenho) ou sobre a foto (1d): texto branco
//     com sombra, traçado com sombra;
//   • 'escuro'  — sem foto (1b): fundo #0B0D12, rótulos cinza, sem sombra;
//   • 'marca'   — a `1u` (1c): degradê azul da marca, rótulos lavanda.
// Os números chegam prontos de `montarDadosGps` — o cartão só distribui.
const GpsCard = ({ variant, data, transparent, formato }) => {
  const { gps } = data;
  const comFoto = Boolean(data.imageUrl) && !transparent;
  const tema = transparent
    ? "sticker"
    : variant === "1u"
    ? "marca"
    : comFoto
    ? "sticker"
    : "escuro";
  const fundo =
    tema === "marca"
      ? Style.gpsFundoMarca
      : tema === "escuro"
      ? Style.gpsFundoEscuro
      : comFoto
      ? Style.gpsFundoFoto
      : "";
  return (
    <>
      {/* `.gradient` de propósito: o modo transparente já esconde qualquer
          `.gradient`, então o PNG sem fundo sai sem este véu também. */}
      {fundo && (
        <div data-el="fundo" className={`${Style.gradient} ${fundo}`} />
      )}
      <div
        data-el="bloco-gps"
        className={Style.gpsBloco}
        data-tema={tema}
        data-formato={formato}
      >
        <div data-el="metricas-gps" className={Style.gpsMetricas}>
          {gps.metricas.map((m) => (
            <div
              key={m.chave}
              data-el={`metrica-${m.chave}`}
              className={Style.gpsMetrica}
            >
              <span>{m.rotulo}</span>
              <strong>{m.valor}</strong>
            </div>
          ))}
          <img
            data-el="tracado"
            className={Style.gpsTracado}
            src={
              tema === "sticker" ? gps.tracado.comSombra : gps.tracado.semSombra
            }
            alt=""
            draggable={false}
          />
        </div>
        <Logo dark={tema !== "sticker"} />
      </div>
    </>
  );
};

// Arrasto pega meio pixel do cartão (= 2px no PNG de 1080×1920). Menos que
// isso é ruído do dedo; mais que isso perde o encaixe fino.
const passoArrasto = (valor) => Math.round(valor * 2) / 2;

/**
 * @param {object} p
 * @param {string} p.variant   id do layout (MEAL_CARD_ORDER / WORKOUT_CARD_ORDER)
 * @param {object} p.data      buildMealShareData / buildWorkoutShareData
 * @param {boolean} [p.transparent]  modo transparente: sem foto de fundo e sem
 *   degradês — o PNG sai com alfa e só as infos ficam. O quadriculado que o
 *   usuário vê é do PREVIEW (fica no elemento PAI, fora do nó capturado), então
 *   nunca entra no arquivo.
 * @param {boolean} [p.editavel]  este cartão responde ao dedo no modo estúdio
 *   (só o do meio do carrossel; as prévias de outras telas continuam inertes).
 */
const ShareCard = forwardRef(
  (
    {
      variant,
      data,
      className = "",
      transparent = false,
      editavel = false,
      /* 'STORY' (9:16, o padrão) ou 'FEED' (4:5). Muda a proporção do cartão e
       a zona útil; o CSS das artes não precisa saber qual é, porque as
       variáveis `--story-safe-*` continuam com o mesmo nome. */
      formato = "STORY",
    },
    ref
  ) => {
    // No modo transparente a foto é ignorada de propósito — e o fallback também
    // não entra: um "TREINO" marca-d'água num PNG transparente seria sujeira.
    const hasImage = Boolean(data?.imageUrl) && !transparent;

    // ── estúdio ────────────────────────────────────────────────────────────────
    // Cada cartão assina só a SUA fatia: arrastar um elemento no cartão 4 não
    // pode re-renderizar os outros 8 do carrossel a cada quadro do dedo.
    const { aplicar, modo, ajustes, selecionado } = useEstudioDoCard(variant);
    const interno = useRef(null);
    const arraste = useRef(null);
    const emEdicao = editavel && modo;

    const guardarRef = (node) => {
      interno.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    // Sem lista de dependências de propósito: o efeito só roda quando o cartão
    // re-renderiza, e ele re-renderiza exatamente quando a fatia dele muda. Ele
    // reescreve tudo do zero, então rodar de novo é idempotente.
    useLayoutEffect(() => {
      const raiz = interno.current;
      if (!raiz) return;
      // A AMOSTRA fica no desenho original: os ajustes do estúdio são do ARQUIVO
      // (shareCanvas → aplicarNaCaptura). Na tela eles só aparecem com o editor
      // aberto, senão não daria para arrumar vendo o resultado. Com o editor
      // fechado esta chamada apenas LIMPA o que ficou de uma sessão anterior.
      aplicarAjustes(raiz, ajustes, modo && aplicar);
      garantirVarsBase(raiz);
      // A moldura é sempre reconstruída aqui e apagada do clone na captura —
      // nunca sobra carimbada no arquivo.
      limparEstudio(raiz);
      if (emEdicao && selecionado) {
        const alvo = resolverElemento(raiz, selecionado);
        if (alvo) alvo.setAttribute(ATTR_SEL, "1");
      }
    });

    const aoApontar = (evento) => {
      const raiz = interno.current;
      if (!raiz) return;
      const alvo = evento.target instanceof Element ? evento.target : raiz;
      // O degradê cobre o cartão inteiro (inset: 0), então toda área "vazia" bate
      // nele. Mover um véu decorativo não serve para nada — o toque no vazio vale
      // como toque no cartão.
      const decorativo = alvo.getAttribute?.("data-el") === "fundo";
      const caminho = decorativo ? RAIZ : caminhoDoElemento(raiz, alvo) || RAIZ;
      estudioApi.selecionar(variant, caminho);
      // O cartão é a moldura da foto: arrastá-lo mudaria a tela e não o arquivo
      // (o html2canvas captura o próprio nó). Seleciona, mas não arrasta.
      if (caminho === RAIZ) {
        arraste.current = null;
        return;
      }
      const patch = patchCompleto(ajustes?.[caminho]);
      // O preview encolhe o cartão em telas baixas (transform: scale(.85)). Sem
      // dividir pela escala o elemento andaria menos que o dedo.
      const largura =
        raiz.getBoundingClientRect().width || raiz.offsetWidth || 1;
      const escala = largura / (raiz.offsetWidth || largura);
      arraste.current = {
        caminho,
        x0: evento.clientX,
        y0: evento.clientY,
        dx0: Number(patch.dx) || 0,
        dy0: Number(patch.dy) || 0,
        escala: escala > 0 ? escala : 1,
        quadro: 0,
        pendente: null,
      };
      try {
        raiz.setPointerCapture(evento.pointerId);
      } catch {
        /* sem captura — segue */
      }
      evento.preventDefault();
    };

    const aoMover = (evento) => {
      const atual = arraste.current;
      if (!atual) return;
      atual.pendente = {
        dx: passoArrasto(
          atual.dx0 + (evento.clientX - atual.x0) / atual.escala
        ),
        dy: passoArrasto(
          atual.dy0 + (evento.clientY - atual.y0) / atual.escala
        ),
      };
      // Um commit por quadro: o dedo dispara pointermove bem mais rápido que a
      // tela desenha, e cada commit é um render + reescrita de estilo.
      if (atual.quadro) return;
      atual.quadro = requestAnimationFrame(() => {
        const vivo = arraste.current;
        if (!vivo) return;
        vivo.quadro = 0;
        if (vivo.pendente)
          estudioApi.ajustar(variant, vivo.caminho, vivo.pendente);
      });
    };

    const aoSoltar = () => {
      const atual = arraste.current;
      if (!atual) return;
      if (atual.quadro) cancelAnimationFrame(atual.quadro);
      if (atual.pendente)
        estudioApi.ajustar(variant, atual.caminho, atual.pendente);
      arraste.current = null;
    };

    return (
      <div
        ref={guardarRef}
        className={[
          Style.shareCard,
          !hasImage && !transparent ? Style.shareCardFallback : "",
          transparent ? Style.shareCardTransparente : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          ...(SAFE_AREA_VARS_POR_FORMATO[formato] ||
            SAFE_AREA_VARS_POR_FORMATO.STORY),
          /* A altura do cartão vem do formato. A largura é sempre 270 — é o que
       mantém a escala de exportação em 4× nos dois. */
          "--story-card-h": `${
            (FORMATOS[formato] || FORMATOS.STORY).card.HEIGHT
          }px`,
          ...(hasImage
            ? { backgroundImage: `url(${JSON.stringify(data.imageUrl)})` }
            : {}),
        }}
        data-share-card={variant}
        data-transparente={transparent ? "true" : undefined}
        data-studio-modo={emEdicao ? "1" : undefined}
        onPointerDown={emEdicao ? aoApontar : undefined}
        onPointerMove={emEdicao ? aoMover : undefined}
        onPointerUp={emEdicao ? aoSoltar : undefined}
        onPointerCancel={emEdicao ? aoSoltar : undefined}
      >
        {!hasImage && !transparent && (
          <div data-el="marca" className={Style.fallbackMark}>
            {data?.kind === "workout" ? "TREINO" : "SCANFOOD"}
          </div>
        )}
        {variant === "photo" ? null : GPS_CARD_ORDER.includes(variant) &&
          data?.gps ? (
          <GpsCard
            variant={variant}
            data={data}
            transparent={transparent}
            formato={formato}
          />
        ) : data?.kind === "workout" ? (
          <WorkoutCard variant={variant} data={data} />
        ) : (
          <MealCard variant={variant} data={data} />
        )}
      </div>
    );
  }
);

ShareCard.displayName = "ShareCard";

export default ShareCard;
